// Country reference for the programmatic passport-OCR pages.
//
// One entry per jurisdiction that issues its own machine-readable travel
// document. Every value here is public reference data (ISO 3166-1 codes, the
// English country name and demonym) or generic ICAO 9303 structure — there is
// no personal data of any real holder anywhere in this module. The per-page
// example values used to illustrate an MRZ are synthetic and are built in
// $lib/seo/mrz, not stored here.
//
// The list is hand-maintained: there is no generator, so an entry is added,
// corrected or removed by editing the array below. Removing one withdraws a
// page that was published and crawled, so the address it leaves behind is
// declared in ./gone and answers 410 rather than 404 — and the table here is
// the marketing surface alone: which documents the product can read is decided
// by the API, not by this array. Two invariants hold and are
// enforced outside this file — every `slug` is unique (it is the address of a
// prerendered page, so a repeat silently collapses two countries into one) and
// every `alpha3` is unique (it is the identity printed in the MRZ). The build
// guard `scripts/assert-pages.mjs` checks both, and checks that the
// build emitted exactly one page per entry.

export interface Country {
  /** English country name, e.g. "Germany". */
  readonly name: string;
  /** ISO 3166-1 alpha-3 code, e.g. "DEU" — the code printed in the MRZ. */
  readonly alpha3: string;
  /** ISO 3166-1 alpha-2 code, e.g. "DE". */
  readonly alpha2: string;
  /** English demonym, e.g. "German". */
  readonly adjective: string;
  /** URL slug, e.g. "germany" for /germany-passport-ocr. */
  readonly slug: string;
  /**
   * The short form people type instead of the name, e.g. "UK". Set only where
   * the short form is what a search is written in; the page's title and
   * heading lead with it, and everything else keeps the full name.
   */
  readonly shortName?: string;
  /**
   * The issuing-state and nationality code the machine-readable zone prints,
   * set only where it is not the alpha-3 code. ICAO Doc 9303 part 3 keeps a few
   * such codes; Germany's is the one-letter "D", which the zone fills out with
   * "<" to three characters.
   */
  readonly zoneCode?: string;
}

export const countries: readonly Country[] = [
  { name: "Afghanistan", alpha3: "AFG", alpha2: "AF", adjective: "Afghan", slug: "afghanistan" },
  { name: "Albania", alpha3: "ALB", alpha2: "AL", adjective: "Albanian", slug: "albania" },
  { name: "Algeria", alpha3: "DZA", alpha2: "DZ", adjective: "Algerian", slug: "algeria" },
  { name: "Andorra", alpha3: "AND", alpha2: "AD", adjective: "Andorran", slug: "andorra" },
  { name: "Angola", alpha3: "AGO", alpha2: "AO", adjective: "Angolan", slug: "angola" },
  {
    name: "Antigua and Barbuda",
    alpha3: "ATG",
    alpha2: "AG",
    adjective: "Antiguan",
    slug: "antigua-and-barbuda",
  },
  { name: "Argentina", alpha3: "ARG", alpha2: "AR", adjective: "Argentine", slug: "argentina" },
  { name: "Armenia", alpha3: "ARM", alpha2: "AM", adjective: "Armenian", slug: "armenia" },
  { name: "Australia", alpha3: "AUS", alpha2: "AU", adjective: "Australian", slug: "australia" },
  { name: "Austria", alpha3: "AUT", alpha2: "AT", adjective: "Austrian", slug: "austria" },
  { name: "Azerbaijan", alpha3: "AZE", alpha2: "AZ", adjective: "Azerbaijani", slug: "azerbaijan" },
  { name: "Bahamas", alpha3: "BHS", alpha2: "BS", adjective: "Bahamian", slug: "bahamas" },
  { name: "Bahrain", alpha3: "BHR", alpha2: "BH", adjective: "Bahraini", slug: "bahrain" },
  { name: "Bangladesh", alpha3: "BGD", alpha2: "BD", adjective: "Bangladeshi", slug: "bangladesh" },
  { name: "Barbados", alpha3: "BRB", alpha2: "BB", adjective: "Barbadian", slug: "barbados" },
  { name: "Belarus", alpha3: "BLR", alpha2: "BY", adjective: "Belarusian", slug: "belarus" },
  { name: "Belgium", alpha3: "BEL", alpha2: "BE", adjective: "Belgian", slug: "belgium" },
  { name: "Belize", alpha3: "BLZ", alpha2: "BZ", adjective: "Belizean", slug: "belize" },
  { name: "Benin", alpha3: "BEN", alpha2: "BJ", adjective: "Beninese", slug: "benin" },
  { name: "Bhutan", alpha3: "BTN", alpha2: "BT", adjective: "Bhutanese", slug: "bhutan" },
  { name: "Bolivia", alpha3: "BOL", alpha2: "BO", adjective: "Bolivian", slug: "bolivia" },
  {
    name: "Bosnia and Herzegovina",
    alpha3: "BIH",
    alpha2: "BA",
    adjective: "Bosnian",
    slug: "bosnia-and-herzegovina",
  },
  { name: "Botswana", alpha3: "BWA", alpha2: "BW", adjective: "Botswanan", slug: "botswana" },
  { name: "Brazil", alpha3: "BRA", alpha2: "BR", adjective: "Brazilian", slug: "brazil" },
  { name: "Brunei", alpha3: "BRN", alpha2: "BN", adjective: "Bruneian", slug: "brunei" },
  { name: "Bulgaria", alpha3: "BGR", alpha2: "BG", adjective: "Bulgarian", slug: "bulgaria" },
  {
    name: "Burkina Faso",
    alpha3: "BFA",
    alpha2: "BF",
    adjective: "Burkinabe",
    slug: "burkina-faso",
  },
  { name: "Burundi", alpha3: "BDI", alpha2: "BI", adjective: "Burundian", slug: "burundi" },
  {
    name: "Cabo Verde",
    alpha3: "CPV",
    alpha2: "CV",
    adjective: "Cabo Verdean",
    slug: "cabo-verde",
  },
  { name: "Cambodia", alpha3: "KHM", alpha2: "KH", adjective: "Cambodian", slug: "cambodia" },
  { name: "Cameroon", alpha3: "CMR", alpha2: "CM", adjective: "Cameroonian", slug: "cameroon" },
  { name: "Canada", alpha3: "CAN", alpha2: "CA", adjective: "Canadian", slug: "canada" },
  {
    name: "Central African Republic",
    alpha3: "CAF",
    alpha2: "CF",
    adjective: "Central African",
    slug: "central-african-republic",
  },
  { name: "Chad", alpha3: "TCD", alpha2: "TD", adjective: "Chadian", slug: "chad" },
  { name: "Chile", alpha3: "CHL", alpha2: "CL", adjective: "Chilean", slug: "chile" },
  { name: "China", alpha3: "CHN", alpha2: "CN", adjective: "Chinese", slug: "china" },
  { name: "Colombia", alpha3: "COL", alpha2: "CO", adjective: "Colombian", slug: "colombia" },
  { name: "Comoros", alpha3: "COM", alpha2: "KM", adjective: "Comoran", slug: "comoros" },
  { name: "Congo", alpha3: "COG", alpha2: "CG", adjective: "Congolese", slug: "congo" },
  { name: "Costa Rica", alpha3: "CRI", alpha2: "CR", adjective: "Costa Rican", slug: "costa-rica" },
  { name: "Croatia", alpha3: "HRV", alpha2: "HR", adjective: "Croatian", slug: "croatia" },
  { name: "Cuba", alpha3: "CUB", alpha2: "CU", adjective: "Cuban", slug: "cuba" },
  { name: "Cyprus", alpha3: "CYP", alpha2: "CY", adjective: "Cypriot", slug: "cyprus" },
  { name: "Czechia", alpha3: "CZE", alpha2: "CZ", adjective: "Czech", slug: "czechia" },
  {
    name: "Democratic Republic of the Congo",
    alpha3: "COD",
    alpha2: "CD",
    adjective: "Congolese",
    slug: "democratic-republic-of-the-congo",
  },
  { name: "Denmark", alpha3: "DNK", alpha2: "DK", adjective: "Danish", slug: "denmark" },
  { name: "Djibouti", alpha3: "DJI", alpha2: "DJ", adjective: "Djiboutian", slug: "djibouti" },
  { name: "Dominica", alpha3: "DMA", alpha2: "DM", adjective: "Dominican", slug: "dominica" },
  {
    name: "Dominican Republic",
    alpha3: "DOM",
    alpha2: "DO",
    adjective: "Dominican",
    slug: "dominican-republic",
  },
  { name: "Ecuador", alpha3: "ECU", alpha2: "EC", adjective: "Ecuadorian", slug: "ecuador" },
  { name: "Egypt", alpha3: "EGY", alpha2: "EG", adjective: "Egyptian", slug: "egypt" },
  {
    name: "El Salvador",
    alpha3: "SLV",
    alpha2: "SV",
    adjective: "Salvadoran",
    slug: "el-salvador",
  },
  {
    name: "Equatorial Guinea",
    alpha3: "GNQ",
    alpha2: "GQ",
    adjective: "Equatorial Guinean",
    slug: "equatorial-guinea",
  },
  { name: "Eritrea", alpha3: "ERI", alpha2: "ER", adjective: "Eritrean", slug: "eritrea" },
  { name: "Estonia", alpha3: "EST", alpha2: "EE", adjective: "Estonian", slug: "estonia" },
  { name: "Eswatini", alpha3: "SWZ", alpha2: "SZ", adjective: "Swazi", slug: "eswatini" },
  { name: "Ethiopia", alpha3: "ETH", alpha2: "ET", adjective: "Ethiopian", slug: "ethiopia" },
  { name: "Fiji", alpha3: "FJI", alpha2: "FJ", adjective: "Fijian", slug: "fiji" },
  { name: "Finland", alpha3: "FIN", alpha2: "FI", adjective: "Finnish", slug: "finland" },
  { name: "France", alpha3: "FRA", alpha2: "FR", adjective: "French", slug: "france" },
  { name: "Gabon", alpha3: "GAB", alpha2: "GA", adjective: "Gabonese", slug: "gabon" },
  { name: "Gambia", alpha3: "GMB", alpha2: "GM", adjective: "Gambian", slug: "gambia" },
  { name: "Georgia", alpha3: "GEO", alpha2: "GE", adjective: "Georgian", slug: "georgia" },
  {
    name: "Germany",
    alpha3: "DEU",
    alpha2: "DE",
    adjective: "German",
    slug: "germany",
    zoneCode: "D",
  },
  { name: "Ghana", alpha3: "GHA", alpha2: "GH", adjective: "Ghanaian", slug: "ghana" },
  { name: "Greece", alpha3: "GRC", alpha2: "GR", adjective: "Greek", slug: "greece" },
  { name: "Grenada", alpha3: "GRD", alpha2: "GD", adjective: "Grenadian", slug: "grenada" },
  { name: "Guatemala", alpha3: "GTM", alpha2: "GT", adjective: "Guatemalan", slug: "guatemala" },
  { name: "Guinea", alpha3: "GIN", alpha2: "GN", adjective: "Guinean", slug: "guinea" },
  {
    name: "Guinea-Bissau",
    alpha3: "GNB",
    alpha2: "GW",
    adjective: "Bissau-Guinean",
    slug: "guinea-bissau",
  },
  { name: "Guyana", alpha3: "GUY", alpha2: "GY", adjective: "Guyanese", slug: "guyana" },
  { name: "Haiti", alpha3: "HTI", alpha2: "HT", adjective: "Haitian", slug: "haiti" },
  { name: "Honduras", alpha3: "HND", alpha2: "HN", adjective: "Honduran", slug: "honduras" },
  { name: "Hong Kong", alpha3: "HKG", alpha2: "HK", adjective: "Hong Kong", slug: "hong-kong" },
  { name: "Hungary", alpha3: "HUN", alpha2: "HU", adjective: "Hungarian", slug: "hungary" },
  { name: "Iceland", alpha3: "ISL", alpha2: "IS", adjective: "Icelandic", slug: "iceland" },
  { name: "India", alpha3: "IND", alpha2: "IN", adjective: "Indian", slug: "india" },
  { name: "Indonesia", alpha3: "IDN", alpha2: "ID", adjective: "Indonesian", slug: "indonesia" },
  { name: "Iran", alpha3: "IRN", alpha2: "IR", adjective: "Iranian", slug: "iran" },
  { name: "Iraq", alpha3: "IRQ", alpha2: "IQ", adjective: "Iraqi", slug: "iraq" },
  { name: "Ireland", alpha3: "IRL", alpha2: "IE", adjective: "Irish", slug: "ireland" },
  { name: "Israel", alpha3: "ISR", alpha2: "IL", adjective: "Israeli", slug: "israel" },
  { name: "Italy", alpha3: "ITA", alpha2: "IT", adjective: "Italian", slug: "italy" },
  { name: "Ivory Coast", alpha3: "CIV", alpha2: "CI", adjective: "Ivorian", slug: "ivory-coast" },
  { name: "Jamaica", alpha3: "JAM", alpha2: "JM", adjective: "Jamaican", slug: "jamaica" },
  { name: "Japan", alpha3: "JPN", alpha2: "JP", adjective: "Japanese", slug: "japan" },
  { name: "Jordan", alpha3: "JOR", alpha2: "JO", adjective: "Jordanian", slug: "jordan" },
  { name: "Kazakhstan", alpha3: "KAZ", alpha2: "KZ", adjective: "Kazakhstani", slug: "kazakhstan" },
  { name: "Kenya", alpha3: "KEN", alpha2: "KE", adjective: "Kenyan", slug: "kenya" },
  { name: "Kiribati", alpha3: "KIR", alpha2: "KI", adjective: "I-Kiribati", slug: "kiribati" },
  { name: "Kosovo", alpha3: "XKX", alpha2: "XK", adjective: "Kosovar", slug: "kosovo" },
  { name: "Kuwait", alpha3: "KWT", alpha2: "KW", adjective: "Kuwaiti", slug: "kuwait" },
  { name: "Kyrgyzstan", alpha3: "KGZ", alpha2: "KG", adjective: "Kyrgyzstani", slug: "kyrgyzstan" },
  { name: "Laos", alpha3: "LAO", alpha2: "LA", adjective: "Lao", slug: "laos" },
  { name: "Latvia", alpha3: "LVA", alpha2: "LV", adjective: "Latvian", slug: "latvia" },
  { name: "Lebanon", alpha3: "LBN", alpha2: "LB", adjective: "Lebanese", slug: "lebanon" },
  { name: "Lesotho", alpha3: "LSO", alpha2: "LS", adjective: "Basotho", slug: "lesotho" },
  { name: "Liberia", alpha3: "LBR", alpha2: "LR", adjective: "Liberian", slug: "liberia" },
  { name: "Libya", alpha3: "LBY", alpha2: "LY", adjective: "Libyan", slug: "libya" },
  {
    name: "Liechtenstein",
    alpha3: "LIE",
    alpha2: "LI",
    adjective: "Liechtensteiner",
    slug: "liechtenstein",
  },
  { name: "Lithuania", alpha3: "LTU", alpha2: "LT", adjective: "Lithuanian", slug: "lithuania" },
  {
    name: "Luxembourg",
    alpha3: "LUX",
    alpha2: "LU",
    adjective: "Luxembourgish",
    slug: "luxembourg",
  },
  { name: "Macao", alpha3: "MAC", alpha2: "MO", adjective: "Macanese", slug: "macao" },
  { name: "Madagascar", alpha3: "MDG", alpha2: "MG", adjective: "Malagasy", slug: "madagascar" },
  { name: "Malawi", alpha3: "MWI", alpha2: "MW", adjective: "Malawian", slug: "malawi" },
  { name: "Malaysia", alpha3: "MYS", alpha2: "MY", adjective: "Malaysian", slug: "malaysia" },
  { name: "Maldives", alpha3: "MDV", alpha2: "MV", adjective: "Maldivian", slug: "maldives" },
  { name: "Mali", alpha3: "MLI", alpha2: "ML", adjective: "Malian", slug: "mali" },
  { name: "Malta", alpha3: "MLT", alpha2: "MT", adjective: "Maltese", slug: "malta" },
  {
    name: "Marshall Islands",
    alpha3: "MHL",
    alpha2: "MH",
    adjective: "Marshallese",
    slug: "marshall-islands",
  },
  { name: "Mauritania", alpha3: "MRT", alpha2: "MR", adjective: "Mauritanian", slug: "mauritania" },
  { name: "Mauritius", alpha3: "MUS", alpha2: "MU", adjective: "Mauritian", slug: "mauritius" },
  { name: "Mexico", alpha3: "MEX", alpha2: "MX", adjective: "Mexican", slug: "mexico" },
  { name: "Micronesia", alpha3: "FSM", alpha2: "FM", adjective: "Micronesian", slug: "micronesia" },
  { name: "Moldova", alpha3: "MDA", alpha2: "MD", adjective: "Moldovan", slug: "moldova" },
  { name: "Monaco", alpha3: "MCO", alpha2: "MC", adjective: "Monegasque", slug: "monaco" },
  { name: "Mongolia", alpha3: "MNG", alpha2: "MN", adjective: "Mongolian", slug: "mongolia" },
  { name: "Montenegro", alpha3: "MNE", alpha2: "ME", adjective: "Montenegrin", slug: "montenegro" },
  { name: "Morocco", alpha3: "MAR", alpha2: "MA", adjective: "Moroccan", slug: "morocco" },
  { name: "Mozambique", alpha3: "MOZ", alpha2: "MZ", adjective: "Mozambican", slug: "mozambique" },
  { name: "Myanmar", alpha3: "MMR", alpha2: "MM", adjective: "Burmese", slug: "myanmar" },
  { name: "Namibia", alpha3: "NAM", alpha2: "NA", adjective: "Namibian", slug: "namibia" },
  { name: "Nauru", alpha3: "NRU", alpha2: "NR", adjective: "Nauruan", slug: "nauru" },
  { name: "Nepal", alpha3: "NPL", alpha2: "NP", adjective: "Nepali", slug: "nepal" },
  { name: "Netherlands", alpha3: "NLD", alpha2: "NL", adjective: "Dutch", slug: "netherlands" },
  {
    name: "New Zealand",
    alpha3: "NZL",
    alpha2: "NZ",
    adjective: "New Zealander",
    slug: "new-zealand",
  },
  { name: "Nicaragua", alpha3: "NIC", alpha2: "NI", adjective: "Nicaraguan", slug: "nicaragua" },
  { name: "Niger", alpha3: "NER", alpha2: "NE", adjective: "Nigerien", slug: "niger" },
  { name: "Nigeria", alpha3: "NGA", alpha2: "NG", adjective: "Nigerian", slug: "nigeria" },
  {
    name: "North Korea",
    alpha3: "PRK",
    alpha2: "KP",
    adjective: "North Korean",
    slug: "north-korea",
  },
  {
    name: "North Macedonia",
    alpha3: "MKD",
    alpha2: "MK",
    adjective: "Macedonian",
    slug: "north-macedonia",
  },
  { name: "Norway", alpha3: "NOR", alpha2: "NO", adjective: "Norwegian", slug: "norway" },
  { name: "Oman", alpha3: "OMN", alpha2: "OM", adjective: "Omani", slug: "oman" },
  { name: "Pakistan", alpha3: "PAK", alpha2: "PK", adjective: "Pakistani", slug: "pakistan" },
  { name: "Palau", alpha3: "PLW", alpha2: "PW", adjective: "Palauan", slug: "palau" },
  { name: "Palestine", alpha3: "PSE", alpha2: "PS", adjective: "Palestinian", slug: "palestine" },
  { name: "Panama", alpha3: "PAN", alpha2: "PA", adjective: "Panamanian", slug: "panama" },
  {
    name: "Papua New Guinea",
    alpha3: "PNG",
    alpha2: "PG",
    adjective: "Papua New Guinean",
    slug: "papua-new-guinea",
  },
  { name: "Paraguay", alpha3: "PRY", alpha2: "PY", adjective: "Paraguayan", slug: "paraguay" },
  { name: "Peru", alpha3: "PER", alpha2: "PE", adjective: "Peruvian", slug: "peru" },
  { name: "Philippines", alpha3: "PHL", alpha2: "PH", adjective: "Filipino", slug: "philippines" },
  { name: "Poland", alpha3: "POL", alpha2: "PL", adjective: "Polish", slug: "poland" },
  { name: "Portugal", alpha3: "PRT", alpha2: "PT", adjective: "Portuguese", slug: "portugal" },
  { name: "Qatar", alpha3: "QAT", alpha2: "QA", adjective: "Qatari", slug: "qatar" },
  { name: "Romania", alpha3: "ROU", alpha2: "RO", adjective: "Romanian", slug: "romania" },
  { name: "Rwanda", alpha3: "RWA", alpha2: "RW", adjective: "Rwandan", slug: "rwanda" },
  {
    name: "Saint Kitts and Nevis",
    alpha3: "KNA",
    alpha2: "KN",
    adjective: "Kittitian",
    slug: "saint-kitts-and-nevis",
  },
  {
    name: "Saint Lucia",
    alpha3: "LCA",
    alpha2: "LC",
    adjective: "Saint Lucian",
    slug: "saint-lucia",
  },
  {
    name: "Saint Vincent and the Grenadines",
    alpha3: "VCT",
    alpha2: "VC",
    adjective: "Vincentian",
    slug: "saint-vincent-and-the-grenadines",
  },
  { name: "Samoa", alpha3: "WSM", alpha2: "WS", adjective: "Samoan", slug: "samoa" },
  { name: "San Marino", alpha3: "SMR", alpha2: "SM", adjective: "Sammarinese", slug: "san-marino" },
  {
    name: "Sao Tome and Principe",
    alpha3: "STP",
    alpha2: "ST",
    adjective: "Sao Tomean",
    slug: "sao-tome-and-principe",
  },
  { name: "Saudi Arabia", alpha3: "SAU", alpha2: "SA", adjective: "Saudi", slug: "saudi-arabia" },
  { name: "Senegal", alpha3: "SEN", alpha2: "SN", adjective: "Senegalese", slug: "senegal" },
  { name: "Serbia", alpha3: "SRB", alpha2: "RS", adjective: "Serbian", slug: "serbia" },
  { name: "Seychelles", alpha3: "SYC", alpha2: "SC", adjective: "Seychellois", slug: "seychelles" },
  {
    name: "Sierra Leone",
    alpha3: "SLE",
    alpha2: "SL",
    adjective: "Sierra Leonean",
    slug: "sierra-leone",
  },
  { name: "Singapore", alpha3: "SGP", alpha2: "SG", adjective: "Singaporean", slug: "singapore" },
  { name: "Slovakia", alpha3: "SVK", alpha2: "SK", adjective: "Slovak", slug: "slovakia" },
  { name: "Slovenia", alpha3: "SVN", alpha2: "SI", adjective: "Slovenian", slug: "slovenia" },
  {
    name: "Solomon Islands",
    alpha3: "SLB",
    alpha2: "SB",
    adjective: "Solomon Islander",
    slug: "solomon-islands",
  },
  { name: "Somalia", alpha3: "SOM", alpha2: "SO", adjective: "Somali", slug: "somalia" },
  {
    name: "South Africa",
    alpha3: "ZAF",
    alpha2: "ZA",
    adjective: "South African",
    slug: "south-africa",
  },
  {
    name: "South Korea",
    alpha3: "KOR",
    alpha2: "KR",
    adjective: "South Korean",
    slug: "south-korea",
  },
  {
    name: "South Sudan",
    alpha3: "SSD",
    alpha2: "SS",
    adjective: "South Sudanese",
    slug: "south-sudan",
  },
  { name: "Spain", alpha3: "ESP", alpha2: "ES", adjective: "Spanish", slug: "spain" },
  { name: "Sri Lanka", alpha3: "LKA", alpha2: "LK", adjective: "Sri Lankan", slug: "sri-lanka" },
  { name: "Sudan", alpha3: "SDN", alpha2: "SD", adjective: "Sudanese", slug: "sudan" },
  { name: "Suriname", alpha3: "SUR", alpha2: "SR", adjective: "Surinamese", slug: "suriname" },
  { name: "Sweden", alpha3: "SWE", alpha2: "SE", adjective: "Swedish", slug: "sweden" },
  { name: "Switzerland", alpha3: "CHE", alpha2: "CH", adjective: "Swiss", slug: "switzerland" },
  { name: "Syria", alpha3: "SYR", alpha2: "SY", adjective: "Syrian", slug: "syria" },
  { name: "Taiwan", alpha3: "TWN", alpha2: "TW", adjective: "Taiwanese", slug: "taiwan" },
  { name: "Tajikistan", alpha3: "TJK", alpha2: "TJ", adjective: "Tajikistani", slug: "tajikistan" },
  { name: "Tanzania", alpha3: "TZA", alpha2: "TZ", adjective: "Tanzanian", slug: "tanzania" },
  { name: "Thailand", alpha3: "THA", alpha2: "TH", adjective: "Thai", slug: "thailand" },
  { name: "Timor-Leste", alpha3: "TLS", alpha2: "TL", adjective: "Timorese", slug: "timor-leste" },
  { name: "Togo", alpha3: "TGO", alpha2: "TG", adjective: "Togolese", slug: "togo" },
  { name: "Tonga", alpha3: "TON", alpha2: "TO", adjective: "Tongan", slug: "tonga" },
  {
    name: "Trinidad and Tobago",
    alpha3: "TTO",
    alpha2: "TT",
    adjective: "Trinidadian",
    slug: "trinidad-and-tobago",
  },
  { name: "Tunisia", alpha3: "TUN", alpha2: "TN", adjective: "Tunisian", slug: "tunisia" },
  { name: "Turkey", alpha3: "TUR", alpha2: "TR", adjective: "Turkish", slug: "turkey" },
  { name: "Turkmenistan", alpha3: "TKM", alpha2: "TM", adjective: "Turkmen", slug: "turkmenistan" },
  { name: "Tuvalu", alpha3: "TUV", alpha2: "TV", adjective: "Tuvaluan", slug: "tuvalu" },
  { name: "Uganda", alpha3: "UGA", alpha2: "UG", adjective: "Ugandan", slug: "uganda" },
  { name: "Ukraine", alpha3: "UKR", alpha2: "UA", adjective: "Ukrainian", slug: "ukraine" },
  {
    name: "United Arab Emirates",
    alpha3: "ARE",
    alpha2: "AE",
    adjective: "Emirati",
    slug: "united-arab-emirates",
  },
  {
    name: "United Kingdom",
    alpha3: "GBR",
    alpha2: "GB",
    adjective: "British",
    slug: "united-kingdom",
    shortName: "UK",
  },
  {
    name: "United States",
    alpha3: "USA",
    alpha2: "US",
    adjective: "American",
    slug: "united-states",
    shortName: "US",
  },
  { name: "Uruguay", alpha3: "URY", alpha2: "UY", adjective: "Uruguayan", slug: "uruguay" },
  { name: "Uzbekistan", alpha3: "UZB", alpha2: "UZ", adjective: "Uzbekistani", slug: "uzbekistan" },
  { name: "Vanuatu", alpha3: "VUT", alpha2: "VU", adjective: "Ni-Vanuatu", slug: "vanuatu" },
  { name: "Vatican City", alpha3: "VAT", alpha2: "VA", adjective: "Vatican", slug: "vatican-city" },
  { name: "Venezuela", alpha3: "VEN", alpha2: "VE", adjective: "Venezuelan", slug: "venezuela" },
  { name: "Vietnam", alpha3: "VNM", alpha2: "VN", adjective: "Vietnamese", slug: "vietnam" },
  { name: "Yemen", alpha3: "YEM", alpha2: "YE", adjective: "Yemeni", slug: "yemen" },
  { name: "Zambia", alpha3: "ZMB", alpha2: "ZM", adjective: "Zambian", slug: "zambia" },
  { name: "Zimbabwe", alpha3: "ZWE", alpha2: "ZW", adjective: "Zimbabwean", slug: "zimbabwe" },
];

/** The code the zone prints for this country: its own zone code, or its alpha-3. */
export function zoneCodeOf(country: Pick<Country, "alpha3" | "zoneCode">): string {
  return country.zoneCode ?? country.alpha3;
}

const bySlug = new Map<string, Country>(countries.map((c) => [c.slug, c]));

/** Look up a country by its URL slug, or undefined when the slug is unknown. */
export function countryBySlug(slug: string): Country | undefined {
  return bySlug.get(slug);
}

/** Every slug, for route enumeration and the sitemap. */
export function countrySlugs(): readonly string[] {
  return countries.map((c) => c.slug);
}
